import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewEncapsulation, ViewChild, ElementRef, AfterViewChecked, AfterViewInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AppDataService, ChatService, SignalrService } from 'app/services';
import { ChatTypeEnum, ChatMessageTypeEnum, XpressUserRoleEnum } from 'app/models';
import { Utils } from 'app/utils';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss','../../../styles/default-material-theme.scss'],
    //encapsulation: ViewEncapsulation.None
})

export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked{
    public merchantId: number = 0;
    @Input()
    set _merchantId(value: number) {
        this.merchantId = value;
    }

    public chatId: number = 0;
    @Input()
    set _chatId(value: number) {
        this.chatId = value;
        this.getChat();
    }

    public messageTypeId!: ChatMessageTypeEnum;
    @Input()
    set _messageTypeId(value: ChatMessageTypeEnum) {
        this.messageTypeId = value;
    }

    public showChatSideBar: boolean = false;
    @Input()
    set _showChatSideBar(value: boolean) {
        this.showChatSideBar = value;
    }

    @Output() changedShowChatSideBarEmit = new EventEmitter<boolean>();
    changedShowChatSideBar(increased: any) {
        this.changedShowChatSideBarEmit.emit(increased);
    }

    public loading: boolean = false;
    public isSend: boolean = false;

    public chat!: any;

    public message!: string;

    _chatMessageTypeEnum = ChatMessageTypeEnum;

    $groupChat: Observable<any>;
    $groupChatSubject: Subscription | undefined;

    @ViewChild('chatBody') private scrollableDiv!: ElementRef;
    isScrolled = false;

    constructor(
        private messageService: MessageService,
        public chatService: ChatService,
        private signalRService: SignalrService,
        public appData: AppDataService) {

        this.$groupChat = this.signalRService.GroupChatObservable;
    }

    ngOnInit(): void {
        this.signalRService.startConnection().then(() => {
            console.log('signalR Service connected');
            if (this.chatId > 0) {
                this.signalRService.joinGroupChat(this.chatId.toString()).then(() => {
                    this.signalRService.listenToGroupChat();
                    this.$groupChatSubject = this.$groupChat.subscribe((data: string) => {
                        if (this.chatId.toString() === data) {
                            this.getChat();
                        }
                    });
                }, (err) => {
                    console.log(err);
                });
            }
        });
    }

    ngOnDestroy() {
        this.signalRService?.disconnect();
        this.$groupChatSubject?.unsubscribe();
    }

    ngAfterViewChecked() {
        if (this.scrollableDiv && this.chat && !this.isScrolled) {
            this.scrollToBottom();
            this.isScrolled = true;
          }
    }
    scrollToBottom(): void {
        this.scrollableDiv.nativeElement.scrollTop = this.scrollableDiv.nativeElement.scrollHeight;
      }

    public getChat() {
        if (this.chatId > 0) {
            this.loading = true;

            const dto = { ChatId: this.chatId };

            this.chatService.getChat(dto, this.messageService)
                .then(data => {
                    if (!data) {
                        this.loading = false;
                        return;
                    }
                    if (!data.Success) {
                        this.loading = false;
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                        return;
                    }
                    this.chat = data;
                    for (const message of this.chat.Chat.Messages) {
                        message.CreatedAt = Utils.utcToLocaleString(message.CreatedAt);
                    }
                    if (this.chat.LastMessageId !== null) {
                        this.markMessagesAsRead(this.chat.LastMessageId);
                    }
                    this.loading = false;
                    this.isScrolled = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error });
                    console.log(error);
                });
        }
    }

    public markMessagesAsRead(lastMessageId: number) {
        if (lastMessageId > 0) {
            const dto = { ChatId: this.chatId, LastReadMessageId: lastMessageId };

            this.chatService.markMessagesAsRead(dto, this.messageService)
                .then(data => {
                    if (!data) {
                        return;
                    }
                    if (!data.Success) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                        return;
                    }
                })
                .catch(error => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error });
                    console.log(error);
                });
        }
    }

    public sendMessage(event: any) {
        this.message = event.target.value;
        this.addMessage();
        event.preventDefault();
    }

    public addMessage() {
        this.isSend = true;

        const dto = {
            ChatTypeId: ChatTypeEnum.MerchantSupport,
            MerchantId: this.merchantId,
            MessageTypeId: this.messageTypeId,
            Message: this.message
        };

        this.chatService.addMessage(dto, this.messageService)
            .then(data => {
                if (!data) {
                    this.isSend = false;
                    return;
                }
                if (!data.Success) {
                    this.isSend = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.isSend = false;
                this.message = '';
                this.chatId = data.Chat.Id;
            })
            .catch(error => {
                this.isSend = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error });
                console.log(error);
            });
    }

    getChatMessageTitle(chatMessage: any): string {
        let message;
        switch (chatMessage.MessageTypeId) {
            case ChatMessageTypeEnum.SupportToMerchant:
                message = 'Support';
                break;
            case ChatMessageTypeEnum.MerchantToSupport:
                message = 'Merchant';
                break;
            default:
                message = '';
        }

        return message;
    }

    getChatMessageTyp(chatMessage: any): boolean {
        if (this.appData.xpressUserRoleId === XpressUserRoleEnum.Merchant) {
            if (chatMessage.MessageTypeId === ChatMessageTypeEnum.MerchantToSupport ||
                chatMessage.MessageTypeId === ChatMessageTypeEnum.MerchantToCustomer) {
                return true;
            }
            return false;
        }

        if (chatMessage.MessageTypeId === ChatMessageTypeEnum.SupportToCustomer ||
            chatMessage.MessageTypeId === ChatMessageTypeEnum.SupportToMerchant) {
            return true;
        }
        return false;
    }

    cancel() {
        this.showChatSideBar = false;
        this.changedShowChatSideBar(this.showChatSideBar);
        this.signalRService?.disconnect();
        this.$groupChatSubject?.unsubscribe();
    }
}
